import { KeyRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { passwordStandingMessage } from '@jt/domain';
import { useSession } from '../lib/session';

/**
 * Ten days' warning that a password is about to have to change.
 *
 * A password expires ninety days after it was chosen, and on the ninety-first
 * day the account reaches nothing but the change-password screen. That is the
 * right enforcement and the wrong first notification: being stopped mid-task by
 * a rule nobody mentioned reads as a fault, and the reflex is to ring the office
 * rather than to change a password.
 *
 * So the warning arrives while it is still only a warning, it says how long is
 * left, and it links to the one screen that resolves it. By the time the rule
 * bites, nobody should be surprised by it.
 *
 * Renders nothing in the two cases that matter: an account with plenty of time,
 * and a provider-only account with no password to rotate at all — the second
 * decided in the domain rather than here, so every surface agrees about it.
 */
export function PasswordAgeNotice() {
  const { user } = useSession();
  const standing = user?.password;

  if (!standing || standing.state === 'fine' || standing.state === 'not-applicable') return null;

  const message = passwordStandingMessage(standing);
  if (!message) return null;

  const urgent = standing.state === 'expired';

  return (
    <div
      /*
       * `status`, not `alert`. A screen reader should mention this when it
       * reaches it, not interrupt whatever is being read — it is a deadline, not
       * an emergency, and an assertive announcement on every page load would be
       * exhausting.
       */
      role="status"
      className={`flex flex-wrap items-center gap-3 border-b px-4 py-2.5 text-sm md:px-6 ${
        urgent
          ? 'border-danger-200 bg-danger-50 text-danger-700'
          : 'border-accent-200 bg-accent-50 text-accent-700'
      }`}
    >
      <KeyRound className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">{message}</span>
      {/*
        Navy, not orange. The Jua rule allows one orange element per screen and
        it belongs to whatever that page is for — a banner that appears on every
        page would take it from all of them.
      */}
      <Link to="/account" className="btn-secondary shrink-0 px-3 py-1.5 text-xs">
        Change it now
      </Link>
    </div>
  );
}
